"use client";

import { DisabledProvider, useDisabled } from "./useDisabled";
import clsx from "clsx";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./styles/FormControl.module.scss";

import { Tooltip } from "antd";

export const ErrorOrDescription = ({
  error,
  description,
  disabled,
}: {
  error?: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
}) => (
  <AnimatePresence initial={false}>
    {(error || description) && (
      <motion.div
        initial={{
          opacity: 0,
          y: -4,
          maxHeight: 0,
          paddingTop: "0rem",
        }}
        animate={{
          opacity: 1,
          y: 0,
          maxHeight: "5rem",
          paddingTop: "0.1rem",
        }}
        exit={{
          opacity: 0,
          y: -4,
          maxHeight: 0,
          paddingTop: "0rem",
        }}
        className={clsx(styles.errorOrDescription, {
          [styles.disabled]: !!disabled,
          [styles.error]: !!error,
        })}
      >
        {error || description}
      </motion.div>
    )}
  </AnimatePresence>
);

export interface FormControlProps {
  label?: React.ReactNode | string;
  // name isn't strictly required when using standalone form elements
  // but marking it as such simplifies typing across the codebase
  name?: string;
  helpText?: React.ReactNode | string;
  disabled?: boolean;
  description?: React.ReactNode;
  error?: React.ReactNode;
  className?: string;
  optional?: boolean;
  labelExtra?: React.ReactNode | string;
  // it can be useful to wrap in smth else than a label if we have 2 inputs
  // within the same form control, as it gives more control over focus
  WrapperComponent?: "label" | "div";
  id?: string;
}

export function FormControl({
  label,
  name,
  helpText,
  disabled = false,
  description,
  error,
  children,
  className,
  optional,
  labelExtra,
  WrapperComponent = "label",
  id,
}: FormControlProps & { children: React.ReactNode | React.ReactNode[] }) {
  const ctxDisabled = useDisabled() || disabled;
  const isNegative = typeof labelExtra === "number" && labelExtra < 0;

  return (
    <DisabledProvider value={ctxDisabled}>
      <WrapperComponent
        id={id}
        htmlFor={name}
        className={clsx(
          "form-control",
          styles.formControl,
          {
            [styles.disabled]: ctxDisabled,
          },
          className,
        )}
      >
        {label && (
          <div className={styles.label}>
            <div className={styles.labelContent}>
              <div>{label}</div>
              {optional && <span className={styles.optional}>Optional</span>}
              {helpText && (
                <Tooltip title={helpText} key={name}>
                  <QuestionCircleOutlined />
                </Tooltip>
              )}
            </div>
            {labelExtra !== undefined && (
              <span
                className={clsx(styles.labelExtra, {
                  [styles.negative]: isNegative,
                })}
              >
                {labelExtra}
              </span>
            )}
          </div>
        )}
        {children}
        <ErrorOrDescription
          error={error}
          description={description}
          disabled={disabled}
        />
      </WrapperComponent>
    </DisabledProvider>
  );
}
